import { useAccountStore } from 'store'
import { getPermissionTier } from 'utils'

/**
 * Does the workspace's plan cover this signal — i.e. would the API accept it?
 *
 * Mirrors `hasFeature` in `core/lib/utils/tier-permissions-helper.ts:29`, the predicate
 * that 403s a protection save, and ERRS TOWARD COVERED wherever the two could disagree.
 * A false negative here is worse than the 403 it prevents: `settingsToProtection` masks
 * on this, so saying "not covered" about a signal the customer pays for silently turns
 * that protection off on the next save.
 *
 * Two cases that need saying out loud:
 *
 * - **Account not loaded.** The store's `hasFeature` returns false for EVERY feature
 *   until the account arrives (`account-store.ts:325`), which is indistinguishable from
 *   a genuinely uncovered signal. Never guess.
 * - **`legacy`.** Core's table carries `legacy: true` for every `protection_*` key
 *   (`core/config/tier-features.ts:44-85`); the platform's has no `legacy` column at all
 *   and the store falls back to `standard` (`account-store.ts:327`), so the two disagree
 *   about the Pro-only signals. The API is the authority.
 *
 * Trials need no case of their own — `hasFeature` already returns true for all of them.
 */
export const useSignalCoverage = () => {
	const account = useAccountStore((state) => state.account)
	const hasFeature = useAccountStore((state) => state.hasFeature)
	const tier = getPermissionTier(account?.billing?.subscription?.plan.tier)

	return (feature?: string) => {
		if (!feature) return true
		if (!account) return true
		if (tier === 'legacy') return true
		return hasFeature(feature)
	}
}
